import { useQuery, useQueryClient } from 'react-query';
import { Client, Message, Participant } from '@twilio/conversations';
import { HeroConversationMessage, Associate } from '../../model';
import { getTwilioAuthToken } from '../hero/heroApiClient';
import { chatActions, useAppDispatch } from '../../redux';
import { useContext } from 'react';
import { AuthenticationContext } from '../../components';

const ONE_DAY_MS = 24 * 60 * (60 * 1000);

export const useGetTwilioClient = () => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  return useQuery(
    ['getTwilioClient'],
    async () => {
      const twilioAuthToken = await getTwilioAuthToken();
      const client = new Client(twilioAuthToken);

      const invalidateTwilioConversationMessages = (conversationId: string) => {
        queryClient.invalidateQueries({
          queryKey: ['getTwilioConversationMessages', conversationId],
        });
      };

      const invalidateTwilioConversation = async (conversationId: string) => {
        queryClient.invalidateQueries({
          queryKey: ['getTwilioConversation', conversationId],
        });
      };

      const invalidateUnreadMessagesCount = (conversationId: string) =>
        queryClient.invalidateQueries({
          queryKey: [
            'getTwilioConversationUnreadMessagesCount',
            conversationId,
          ],
        });

      const getCachedAssociate = () =>
        queryClient.getQueryData<Associate>('getAssociate');

      const onTyping = ({
        participant,
        isTyping,
      }: {
        participant: Participant;
        isTyping: boolean;
      }) => {
        const associate = getCachedAssociate();
        const isShopperTyping = associate?.username !== participant.identity;
        if (isShopperTyping) {
          queryClient.setQueryData(
            ['getIsShopperTyping', participant.conversation.sid],
            isTyping,
          );
        }
      };

      const onMessageAdded = async (message: HeroConversationMessage) => {
        const associate = getCachedAssociate();
        const wasMessageSentByAssociate =
          associate?.username === message.author;
        const isLocalMessage = Boolean(message.attributes.localMessageId);
        const twilioConversationId = message.conversation.sid;

        invalidateTwilioConversationMessages(twilioConversationId);
        if (isLocalMessage) {
          dispatch(
            chatActions.updateLocalMessageIndex({ localMessage: message }),
          );
        }
        if (!wasMessageSentByAssociate) {
          invalidateUnreadMessagesCount(twilioConversationId);
        }
      };

      client.on('messageUpdated', ({ message }) =>
        onMessageAdded(message as Message & HeroConversationMessage),
      );

      client.on('messageAdded', (message) =>
        onMessageAdded(message as Message & HeroConversationMessage),
      );

      client.on('typingStarted', (participant) =>
        onTyping({ participant, isTyping: true }),
      );

      client.on('typingEnded', (participant) =>
        onTyping({ participant, isTyping: false }),
      );

      client.on('conversationAdded', (conversation) => {
        const twilioConversationId = conversation.sid;
        invalidateTwilioConversation(twilioConversationId);
        invalidateTwilioConversationMessages(twilioConversationId);
        invalidateUnreadMessagesCount(twilioConversationId);
      });

      return client;
    },
    {
      staleTime: ONE_DAY_MS,
      suspense: true,
      enabled: isAuthenticated,
    },
  );
};
